import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { MODULES, PERMISSION_LEVELS, roleDefaults, getEffectivePermissions } from "../lib/permissions";
import { userAPI } from "../lib/auth";
import { cn } from "../lib/utils";
import { toast } from "sonner";
import { RotateCcw } from "lucide-react";

const LEVEL_STYLES = {
  none: "bg-slate-200 text-slate-700 border-slate-300",
  view: "bg-blue-600 text-white border-blue-600",
  edit: "bg-emerald-600 text-white border-emerald-600",
};

export function UserPermissionsDialog({ user, open, onOpenChange, onSaved }) {
  const [perms, setPerms] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) setPerms(getEffectivePermissions(user));
  }, [user]);

  const handleSave = async () => {
    try {
      setSaving(true);
      await userAPI.updatePermissions(user.id, perms);
      toast.success(`Permissions updated for ${user.full_name}`);
      onOpenChange(false);
      onSaved();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update permissions");
    } finally {
      setSaving(false);
    }
  };

  if (!user) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="permissions-dialog">
        <DialogHeader>
          <DialogTitle>Module Permissions — {user.full_name}</DialogTitle>
          <DialogDescription>
            Set access level per module for this user. Overrides their role defaults.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-1.5">
          {MODULES.map(({ key, label }) => (
            <div key={key} className="flex items-center justify-between py-1.5 px-2 rounded hover:bg-slate-50" data-testid={`perm-row-${key}`}>
              <span className="text-sm font-medium text-slate-800">{label}</span>
              <div className="flex rounded-md overflow-hidden border border-slate-200">
                {PERMISSION_LEVELS.map(({ value, label: lvlLabel }) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setPerms({ ...perms, [key]: value })}
                    className={cn(
                      "px-3 py-1 text-xs font-medium transition-colors",
                      perms[key] === value ? LEVEL_STYLES[value] : "bg-white text-slate-500 hover:bg-slate-100"
                    )}
                    data-testid={`perm-${key}-${value}`}
                  >
                    {lvlLabel}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-between pt-2 border-t">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setPerms(roleDefaults(user.role))}
            data-testid="reset-role-defaults-button"
          >
            <RotateCcw className="w-3.5 h-3.5 mr-1.5" />
            Reset to {user.role} defaults
          </Button>
          <div className="flex space-x-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="cancel-permissions-button">
              Cancel
            </Button>
            <Button type="button" onClick={handleSave} disabled={saving} data-testid="save-permissions-button">
              {saving ? "Saving..." : "Save Permissions"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
